import { useEffect, useState } from 'react';
import type { CSSProperties, JSX } from 'react';
import { Button } from '../../../_components/Button.js';
import type { Palette } from '../../../_theme/palette.js';
import { MediaBlock } from './MediaBlock.js';
import { SocialLinksRow } from './SocialLinksRow.js';
import { SuccessCheckmark } from './SuccessCheckmark.js';
import { usePipe } from './PipingProvider.js';
import type { SurveyPage } from '../types.js';

interface ThankYouViewProps {
  palette: Palette;
  page?: SurveyPage;
  onClose: () => void;
}

export function ThankYouView({ palette, page, onClose }: ThankYouViewProps): JSX.Element {
  const pipe = usePipe();
  const primaryMedia = page?.media?.[0];
  const redirectUrl = page?.redirectUrl?.trim();
  const redirectDelay = Math.max(0, page?.redirectDelay ?? 0);
  const countdown = useRedirectCountdown(redirectUrl, redirectDelay);
  const title = pipe(page?.title) || 'Thank you!';
  const description = pipe(page?.description) || 'Your response has been recorded.';

  return (
    <div style={wrapperStyle()}>
      {primaryMedia ? (
        <div style={{ marginBottom: 24, width: '100%' }}>
          <MediaBlock media={primaryMedia} radius={12} maxHeight={220} />
        </div>
      ) : (
        <SuccessCheckmark accentColor={palette.primary} />
      )}
      <h1 style={titleStyle(palette)}>{title}</h1>
      <p style={descriptionStyle(palette)}>{description}</p>
      {redirectUrl && (
        <RedirectCTA
          palette={palette}
          url={redirectUrl}
          label={page?.redirectLabel}
          countdown={countdown}
        />
      )}
      {page?.socialLinks && <SocialLinksRow palette={palette} links={page.socialLinks} />}
      <div style={{ marginTop: 24 }}>
        <Button palette={palette} variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}

interface RedirectCTAProps {
  palette: Palette;
  url: string;
  label?: string;
  countdown: number | null;
}

function RedirectCTA({ palette, url, label, countdown }: RedirectCTAProps): JSX.Element {
  const showCountdown = countdown !== null && countdown > 0;
  return (
    <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <a href={url} target="_top" rel="noopener noreferrer" style={ctaStyle(palette)}>
        {label?.trim() || 'Continue'}
      </a>
      {showCountdown && (
        <span style={countdownStyle(palette)}>Redirecting in {countdown}s…</span>
      )}
    </div>
  );
}

function useRedirectCountdown(url: string | undefined, delaySeconds: number): number | null {
  const [seconds, setSeconds] = useState<number | null>(url && delaySeconds > 0 ? delaySeconds : null);

  useEffect(() => {
    if (!url || delaySeconds <= 0) return;
    let remaining = delaySeconds;
    setSeconds(remaining);
    const intervalId = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        clearInterval(intervalId);
        setSeconds(0);
        if (typeof window !== 'undefined') window.top?.location.assign(url);
        return;
      }
      setSeconds(remaining);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [url, delaySeconds]);

  return seconds;
}

function wrapperStyle(): CSSProperties {
  return {
    padding: '48px 32px 32px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  };
}

function titleStyle(palette: Palette): CSSProperties {
  return { margin: 0, fontSize: 28, fontWeight: 700, color: palette.text, lineHeight: 1.2 };
}

function descriptionStyle(palette: Palette): CSSProperties {
  return { margin: '12px 0 0', fontSize: 16, color: palette.textMuted, lineHeight: 1.6, maxWidth: 460 };
}

function ctaStyle(palette: Palette): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '10px 22px',
    background: palette.primary,
    color: palette.onPrimary,
    borderRadius: palette.radius,
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 600,
  };
}

function countdownStyle(palette: Palette): CSSProperties {
  return { fontSize: 12, color: palette.textMuted };
}
